import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { X, Filter } from 'lucide-react';

export default function ZohoReportFilters({ allFields, filterFields, onFilterFieldsChange, submissions, filters, onFiltersChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const getUniqueValues = (field) => {
    const vals = new Set();
    submissions.forEach(s => {
      const v = (s.raw_payload || {})[field];
      if (v !== undefined && v !== null && v !== '') vals.add(String(v));
    });
    return [...vals].sort();
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  const setFilter = (field, value) => {
    onFiltersChange(prev => ({ ...prev, [field]: value === '_all' ? '' : value }));
  };

  const clearFilters = () => onFiltersChange({});

  const toggleFilterField = (field) => {
    const next = filterFields.includes(field)
      ? filterFields.filter(f => f !== field)
      : [...filterFields, field];
    onFilterFieldsChange(next);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="report-filters">
      {/* Filter field picker */}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="filter-fields-picker">
            <Filter className="h-3 w-3" />
            Filters ({filterFields.length})
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-60 max-h-72 overflow-auto" align="start">
          <p className="text-xs font-medium text-muted-foreground mb-2">Choose fields to filter by</p>
          {allFields.map(f => (
            <div key={f} className="flex items-center gap-2 py-1">
              <Checkbox
                id={`ff-${f}`}
                checked={filterFields.includes(f)}
                onCheckedChange={() => toggleFilterField(f)}
                data-testid={`ff-check-${f}`}
              />
              <Label htmlFor={`ff-${f}`} className="text-sm cursor-pointer flex-1 truncate">{f}</Label>
            </div>
          ))}
        </PopoverContent>
      </Popover>

      {/* Active filter dropdowns */}
      {filterFields.map(col => {
        const values = getUniqueValues(col);
        if (values.length === 0 || values.length > 50) return null;
        return (
          <Select key={col} value={filters[col] || '_all'} onValueChange={v => setFilter(col, v)}>
            <SelectTrigger className="w-auto min-w-[130px] h-8 text-xs" data-testid={`filter-${col}`}>
              <SelectValue placeholder={col} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{col}: All</SelectItem>
              {values.map(v => (
                <SelectItem key={v} value={v}>{v.length > 30 ? v.substring(0, 30) + '...' : v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      })}

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs gap-1" data-testid="clear-filters">
          <X className="h-3 w-3" />Clear
        </Button>
      )}
    </div>
  );
}
